/**
 * Format an axios/fetch error's server-side `detail` payload as a
 * human-readable STRING that is always safe to pass to toast/JSX.
 *
 * Handles every shape our FastAPI backend can produce:
 *   • string  →  as-is                    (HTTPException(400, "Nope"))
 *   • array   →  pydantic 422             ([{loc,msg,type,...}])
 *   • dict    →  nested detail            ({error, detail})
 *   • other   →  JSON.stringify fallback
 *
 * Never returns null/undefined/an object — always a string, so passing the
 * result to `toast.error(...)` or `<div>{result}</div>` cannot crash React
 * (error #31: "Objects are not valid as a React child").
 */
export function formatApiError(e, fallback = "Request failed") {
  const detail = e?.response?.data?.detail;
  if (detail == null) {
    return e?.message ? String(e.message) : fallback;
  }
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    // pydantic v2 validation errors
    const parts = detail
      .map((item) => {
        if (item && typeof item === "object" && item.msg) {
          const loc = Array.isArray(item.loc)
            ? item.loc.filter((x) => x !== "body").join(".")
            : "";
          return loc ? `${loc}: ${item.msg}` : String(item.msg);
        }
        return typeof item === "string" ? item : JSON.stringify(item);
      })
      .filter(Boolean);
    return parts.length ? parts.join(" · ") : fallback;
  }
  if (typeof detail === "object") {
    // Nested HTTPException(400, {"error": ..., "detail": ...})
    if (typeof detail.detail === "string") return detail.detail;
    if (Array.isArray(detail.detail)) {
      return formatApiError({ response: { data: { detail: detail.detail } } }, fallback);
    }
    if (typeof detail.error === "string") return detail.error;
    try { return JSON.stringify(detail); } catch { return fallback; }
  }
  return String(detail);
}
